<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { FwbAlert } from 'flowbite-vue';
import IconSvg from './IconSvg.vue';
import type { Notification } from '@stores/notification';
import { copyToClipboard } from '@base-shared/utils';

export interface NotificationAlertItem extends Omit<Notification, 'id'> {
  notifId: string;
}

const props = defineProps<NotificationAlertItem>();

const emit = defineEmits<{
  (e: 'dismiss', id: string): void;
}>();

const isPaused = ref(false);
const startTime = ref<number>(0);
const remainingTime = ref<number>(0);

const timer = ref<NodeJS.Timeout | string | number | null>();
const progressInterval = ref<NodeJS.Timeout | string | number | null>();

const hasDuration = computed(() => typeof props.duration === 'number' && props.duration > 0);
const hasTitleAndMessage = computed(() => (props.title || props.htmlTitle) && (props.message || props.htmlMessage));
const progress = computed(() => {
  if (!hasDuration.value) {
    return null;
  }

  return (((props.duration || 0) - remainingTime.value) / (props.duration || 0)) * 100;
});

/** Reset timer when {@link NotificationAlertItem} change */
watch(() => props, () => {
  resetTimer();
}, {
  deep: true
});

function dismiss() {
  emit('dismiss', props.notifId);
}

const updateProgress = () => {
  if (!hasDuration.value) {
    return;
  }

  if (!isPaused.value) {
    remainingTime.value = Math.max(0, (props.duration || 0) - (Date.now() - (startTime.value || 0)));
  }
};

function clearTimer() {
  if (timer.value) {
    clearTimeout(timer.value);
    timer.value = null;
  }
}

function clearProgressInterval() {
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
    progressInterval.value = null;
  }
}

function startTimer() {
  if (!hasDuration.value) {
    return;
  }

  startTime.value = Date.now();
  progressInterval.value = setInterval(updateProgress, 10);

  timer.value = setTimeout(() => {
    dismiss();
  }, props.duration);
}

function pauseTimer() {
  if (!timer.value) {
    return;
  }

  if (hasDuration.value) {
    remainingTime.value = props.duration ?? 0;
  }

  isPaused.value = true;
  clearTimer();
  clearProgressInterval();
}

function resumeTimer() {
  isPaused.value = false;
  startTimer();
}

function resetTimer() {
  clearTimer();
  clearProgressInterval();
  startTimer();
}

onMounted(() => {
  startTimer();
});

onUnmounted(() => {
  clearTimer();
  clearProgressInterval();
});
</script>

<template>
  <div class="relative">
    <FwbAlert
      class="notification-alerts-item self-end"
      :class="{
        'bg-primary-50! text-primary-800!': type === 'info',
        'bg-red-50! text-red-800!': type === 'danger',
        'bg-green-100! text-green-800!': type === 'success',
        'bg-orange-50! text-orange-800!': type === 'warning',
        'bg-gray-50! text-gray-800!': type === 'dark',
        'border-primary-200! border-y rounded-none!': showBorder && type === 'info',
        'border-red-200! border-y rounded-none!': showBorder && type === 'danger',
        'border-green-200! border-y rounded-none!': showBorder && type === 'success',
        'border-orange-200! border-y rounded-none!': showBorder && type === 'warning',
        'border-gray-200! border-y rounded-none!': showBorder && type === 'dark',
        'has-border': showBorder,
        'has-title-message': hasTitleAndMessage,
      }"
      icon
      data-component-element-id="notification-alerts-item-alert"
      :closable="closable"
      :type="type"
      @close="dismiss"
      @mouseover="pauseTimer"
      @mouseleave="resumeTimer"
    >
      <template
        v-if="icon"
        #icon
      >
        <IconSvg
          :name="icon"
          class="w-5! h-6!"
        />
      </template>
      <div class="flex flex-col gap-2 text-left">
        <div
          v-if="htmlTitle"
          class="font-semibold text-base"
          v-html="htmlTitle"
        />
        <div
          v-else-if="title"
          class="font-semibold text-base"
        >
          {{ $t(title) }}
        </div>
        <!-- If message has additional formatting -->
        <div
          v-if="htmlMessage"
          :class="{ 'ml-[-30px]': title || htmlTitle }"
          v-html="htmlMessage"
        />
        <!-- If normal string -->
        <div
          v-else-if="message"
          :class="{ 'ml-[-30px]': title || htmlTitle }"
        >
          {{ $t(message) }}
        </div>
        <div
          v-if="requestId"
          class="flex items-center ml-[-30px]"
        >
          <span>{{ $t('texts.requestId') }} {{ requestId }}</span>
          <button
            class="border-none bg-transparent cursor-pointer"
            @click="copyToClipboard(requestId)"
          >
            <IconSvg
              name="flowbite:file-copy-alt-solid"
              class="w-5! h-5!"
            />
          </button>
        </div>
      </div>
      <template #close-icon>
        <button
          v-if="closable"
          type="button"
          class="ml-auto -mr-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex h-8 w-8 border-none cursor-pointer"
          :class="{
            'self-start': hasTitleAndMessage,
            'text-blue-800! bg-blue-50! !hover:bg-blue-200': type === 'info',
            'text-red-800! bg-red-50! !hover:bg-red-200': type === 'danger',
            'text-green-800! bg-green-100! !hover:bg-green-200': type === 'success',
            'text-orange-800! bg-orange-50! !hover:bg-orange-200': type === 'warning',
            'text-gray-800! bg-gray-50! !hover:bg-gray-200': type === 'dark',
          }"
          @click="dismiss"
        >
          <IconSvg
            name="flowbite:close-outline"
            class="w-5! h-5!"
          />
        </button>
      </template>
    </FwbAlert>

    <div
      v-if="hasDuration"
      class="absolute bottom-0 h-0.5"
      data-component-element-id="notification-alerts-item-progress"
      :class="{
        'bg-blue-300': type === 'info',
        'bg-red-300': type === 'danger',
        'bg-green-300': type === 'success',
        'bg-orange-300': type === 'warning',
        'bg-gray-300': type === 'dark',
      }"
      :style="{ width: progress + '%' }"
    />
  </div>
</template>

<style lang="css" scoped>
@reference '@base-shared/assets/css/tailwind.css';

.notification-alerts-item {
  & :deep( > div:first-child) {
    @apply h-6;
  }

  /* For both title and message present, align the icon and the content */
  /* with `self-start` instead of the default `items-center` */
  &.has-title-message {
    & :deep( > div:first-child) {
      @apply self-start;
    }
  }
}
</style>
